import React from 'react';
import { ClockIcon, UsersIcon, PackageIcon, LightbulbIcon, ZapIcon, MessageCircleIcon } from 'lucide-react';
const rows = [{
  k: 'What they do',
  v: 'Each answers one prompt in the frame, hitting all three parts on purpose.'
}, {
  k: 'Turns',
  v: 'Volunteers first, then round the room. Ten seconds of silent think-time before each.'
}, {
  k: 'Score',
  v: 'The group raises a finger for each part they heard — three fingers is a full P.O.P.'
}, {
  k: 'Watch',
  v: 'The missing punch: answers that just stop. Cue the closing line at the 40-second mark.'
}];
const levels = [{
  dots: 1,
  body: 'Point + punch only, about 20 seconds'
}, {
  dots: 2,
  body: 'Full P.O.P. in 45 seconds'
}, {
  dots: 3,
  body: 'Full P.O.P. with an example inside the reason'
}];
const dials = [{
  name: 'Subject',
  set: 'person · place · thing · idea · moment',
  pick: 'thing'
}, {
  name: 'Time',
  set: 'past · present · future',
  pick: 'future'
}, {
  name: 'Angle',
  set: 'love it · hate it · would change …',
  pick: 'would change'
}, {
  name: 'Scale',
  set: 'you · your town · the whole world',
  pick: 'world'
}, {
  name: 'Mode',
  set: 'opinion · story · explain how · predict',
  pick: 'predict'
}];
function Dots({
  n
}: {
  n: number;
}) {
  return <span className="flex gap-0.5" aria-label={`Level ${n} of 3`} data-id="element-304" data-mp="3l5wnz1nr6fpm">
      {[1, 2, 3].map(i => <span key={i} className={`h-2 w-2 rounded-full ${i <= n ? 'bg-teal' : 'bg-cloud-dark'}`} data-id="element-305" data-mp="3h5dsmxmnirio" />)}
    </span>;
}
function PageFrame({
  tag,
  title,
  band,
  children
}: {
  tag: string;
  title: string;
  band: string;
  children: React.ReactNode;
}) {
  return <article className="overflow-hidden rounded-2xl bg-white text-ink shadow-book" data-id="element-306" data-mp="7y340ke57t78">
      <div className={`flex items-center justify-between px-5 py-2.5 ${band}`} data-id="element-307" data-mp="3xsxn6mfv0ru">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-308" data-mp="10f5n5p4hwn0f">{tag}</span>
        <span className="text-[10px] font-semibold uppercase tracking-widest opacity-80" data-id="element-309" data-mp="10h0atb75it87">Vol. 2 · Thinking on Your Feet</span>
      </div>
      <div className="p-5 sm:p-6" data-id="element-310" data-mp="2pw14zcplgoog">
        <h3 className="font-display text-xl font-extrabold leading-tight tracking-tight sm:text-2xl" data-id="element-311" data-mp="2px2ugm7cxdpp">{title}</h3>
        {children}
      </div>
    </article>;
}
export function SamplePlan() {
  return <section aria-labelledby="sample-heading" className="w-full bg-cloud" data-id="element-312" data-mp="2g67xamskotph">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20" data-id="element-313" data-mp="2l49pbx7z83d3">
        <div className="text-center" data-id="element-314" data-mp="2762y2zxj91me">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-coral-deep" data-id="element-315" data-mp="2750bh478tlca">Look inside</p>
          <h2 id="sample-heading" className="mt-3 font-display text-3xl font-extrabold text-ink sm:text-5xl" data-id="element-316" data-mp="2c6h8gomlwnnl">
            One real session, one real tool
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate" data-id="element-317" data-mp="225x59gfj192q">
            From Volume 2. The first page is a session you can run tonight. The
            second is the Topic Engine — the reason you never run out of prompts
            again. Every one of the 134 sessions follows this same one-page shape.
          </p>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-2" data-id="element-318" data-mp="1kxnw41kp2ayf">
          <PageFrame tag="p17 · Session" title="P.O.P. in Practice: one prompt, one clean 45-second answer" band="bg-amber text-ink" data-id="element-319" data-mp="1fz2txwlvth7c">
            <p className="mt-2 text-[13px] text-slate" data-id="element-320" data-mp="1uoihl3q496it">
              <span className="font-semibold text-ink" data-id="element-321" data-mp="1pw7ext3zr6mk">Objective:</span> drill the P.O.P. frame — Point, One reason, Punch — until a finished answer comes out automatically.
            </p>

            <ul className="mt-3 flex flex-wrap gap-1.5 text-[11px] font-semibold text-ink" data-id="element-322" data-mp="1zmtp37sxv4xv">
              {['Students', 'Club', 'Adults'].map((t, i) => <li key={t} className={`rounded-full px-2 py-0.5 uppercase tracking-wider ${i === 0 ? 'bg-amber-soft' : i === 1 ? 'bg-teal-soft' : 'bg-coral-soft'}`} data-id="element-323" data-mp="2djz3pw6pxc4q" data-mpi={i}>
                  {t}
                </li>)}
              <li className="flex items-center gap-1 rounded-full bg-cloud px-2 py-0.5" data-id="element-324" data-mp="fq4rqdatkorr">
                <UsersIcon className="h-3 w-3" aria-hidden="true" data-id="element-325" data-mp="fsh5sfdgiezj" /> Any size
              </li>
              <li className="flex items-center gap-1 rounded-full bg-cloud px-2 py-0.5" data-id="element-326" data-mp="kraii5kx61yn">
                <ClockIcon className="h-3 w-3" aria-hidden="true" data-id="element-327" data-mp="pp2f3cwiec96" /> 12 min
              </li>
              <li className="flex items-center gap-1 rounded-full bg-cloud px-2 py-0.5" data-id="element-328" data-mp="umm1j03d1vc7">
                <PackageIcon className="h-3 w-3" aria-hidden="true" data-id="element-329" data-mp="3t6kkzesq196y" /> Prompt bank + timer
              </li>
            </ul>

            <div className="mt-4 flex gap-2.5 rounded-xl bg-teal-soft p-3" data-id="element-330" data-mp="1wdg8zdj2ute">
              <MessageCircleIcon className="mt-0.5 h-4 w-4 flex-none text-teal-deep" aria-hidden="true" data-id="element-331" data-mp="6w840u20b4a8" />
              <p className="text-[12.5px] leading-snug text-ink" data-id="element-332" data-mp="bpuc08x86jzp">
                <span className="block text-[10px] font-bold uppercase tracking-widest text-teal-deep" data-id="element-333" data-mp="3adgntjgaoe7l">How to explain it</span>
                “Pull a prompt, take three breaths, then give me P.O.P. — point, one reason, punch, in about 45 seconds. I&apos;ll listen for all three parts.”
              </p>
            </div>

            <dl className="mt-3 grid gap-1.5 text-[12.5px] text-slate" data-id="element-334" data-mp="1shek8px8aizq">
              {rows.map(r => <div key={r.k} data-id="element-335" data-mp="1nfz153em2mjz">
                  <dt className="inline font-semibold text-ink" data-id="element-336" data-mp="1iipaxljee6v3">{r.k}: </dt>
                  <dd className="inline" data-id="element-337" data-mp="1ikteg2ddx3uu">{r.v}</dd>
                </div>)}
            </dl>

            <p className="mt-3 flex gap-2 text-[12.5px] text-ink" data-id="element-338" data-mp="1dlgubpdweavy">
              <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-coral text-[9px] font-bold text-white" data-id="element-339" data-mp="18rclt1ozhusr">!</span>
              <span data-id="element-340" data-mp="zshgrsxoeqr1">
                <span className="font-semibold" data-id="element-341" data-mp="zt0nselsfuqa">Common mistake → fix:</span> smuggling in a second and third reason. Hold them to one; it&apos;s harder and better.
              </span>
            </p>

            <ol className="mt-4 grid gap-1.5" data-id="element-342" data-mp="ptrvrrahgq6k">
              {levels.map(l => <li key={l.dots} className="flex items-center gap-2.5 rounded-lg bg-cloud px-3 py-2" data-id="element-343" data-mp="g5we1u2jwq65">
                  <Dots n={l.dots} data-id="element-344" data-mp="l61yoqaelceq" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-ink" data-id="element-345" data-mp="l73j3ldb7b94">L{l.dots}</span>
                  <span className="text-[12.5px] leading-snug text-slate" data-id="element-346" data-mp="77kbqusbo3xv">{l.body}</span>
                </li>)}
            </ol>
          </PageFrame>

          <PageFrame tag="p27 · Framework" title="The Topic Engine: 5 dials that spin endless prompts" band="bg-ink text-white" data-id="element-347" data-mp="ywnl2oz2g4hd">
            <p className="mt-2 text-[13px] text-slate" data-id="element-348" data-mp="13vi7e0w8sb68">
              Spin one setting from each dial and read them together — you have a
              prompt. The combinations run into the thousands, so no two sessions repeat.
            </p>

            <ol className="mt-4 grid gap-2" data-id="element-349" data-mp="1hw1en5svvd7c">
              {dials.map((d, i) => <li key={d.name} className="grid grid-cols-[28px_1fr_auto] items-center gap-3 rounded-xl border border-cloud-dark p-2.5" data-id="element-350" data-mp="1mmht8t8fx4va" data-mpi={i}>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink font-display text-xs font-extrabold text-amber" data-id="element-351" data-mp="37bvbnx75ze0g">
                    {i + 1}
                  </span>
                  <span className="text-[12.5px] leading-snug text-ink" data-id="element-352" data-mp="3h1on8y7b3t4p">
                    <span className="font-semibold" data-id="element-353" data-mp="3c3wxoau4wj3o">{d.name}</span>
                    <span className="block text-[11.5px] text-slate" data-id="element-354" data-mp="3c5q4pr8nt5c7">{d.set}</span>
                  </span>
                  <span className="rounded-full bg-amber px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ink" data-id="element-355" data-mp="2ofvttf7whqul">{d.pick}</span>
                </li>)}
            </ol>

            <div className="mt-4 rounded-xl bg-ink p-4 text-white" data-id="element-356" data-mp="2ya5m6wwuep5b">
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber" data-id="element-357" data-mp="2tccj4cqu6b99">The engine just built</span>
              <p className="mt-1 font-display text-lg font-extrabold leading-snug" data-id="element-358" data-mp="dk6iizmtz28i">“What everyday object will vanish in twenty years?”</p>
            </div>

            <div className="mt-4 grid gap-2 text-[12.5px]" data-id="element-359" data-mp="226eyuxe3bt0m">
              <p className="flex gap-2 text-ink" data-id="element-360" data-mp="223ssv26ullki">
                <ZapIcon className="mt-0.5 h-4 w-4 flex-none text-amber-deep" aria-hidden="true" data-id="element-361" data-mp="275rvg9urkege" />
                <span data-id="element-362" data-mp="2c2s14jnpbf6z"><span className="font-semibold" data-id="element-363" data-mp="2c31ijiuoke12">Quick Win:</span> make five paper strips, one per dial, and draw one from each live. The room loves watching the prompt get built.</span>
              </p>
              <p className="flex gap-2 text-ink" data-id="element-364" data-mp="2lsuu4jutot5b">
                <LightbulbIcon className="mt-0.5 h-4 w-4 flex-none text-teal-deep" aria-hidden="true" data-id="element-365" data-mp="2pv81fcp7wz47" />
                <span data-id="element-366" data-mp="2uukkdmcyxm1i"><span className="font-semibold" data-id="element-367" data-mp="2uukmpt0fxy4o">Plus:</span> three prompt banks of 60 (everyday, what-if, picture &amp; object) when you&apos;d rather just read one aloud.</span>
              </p>
            </div>
          </PageFrame>
        </div>

        <p className="mt-6 text-center text-[13px] text-slate" data-id="element-368" data-mp="10huqv5qiieun">
          Volume 2 of 6 · sessions, frameworks, 180 prompts, 7 worksheets and a one-page field card
        </p>
      </div>
    </section>;
}